
import { FC } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Mail } from "lucide-react";

interface TeamMember {
  image: string;
  alt: string;
}

interface TeamMembersGridProps {
  members: TeamMember[];
}

const TeamMembersGrid: FC<TeamMembersGridProps> = ({ members }) => {
  const navigate = useNavigate();

  const handleImageClick = (alt: string) => {
    console.log("Clicked on image with alt:", alt); // Debug logging

    if (alt === "Owl #9" || alt === "Innovation Expert") {
      navigate("/trey-profile");
    } else if (alt === "Owl #12" || alt === "Supply Chain and Law Expert") {
      console.log("Navigating to Morgan's profile"); // Debug logging
      navigate("/morgan-profile");
    } else if (alt === "CTO Team Member") {
      // Profile removed
    } else if (alt === "GTM Advisor" || alt === "Owl #2") {
      navigate("/chris-profile");
    } else if (alt === "Strategic Sourcing" || alt === "Owl #6") {
      navigate("/karen-profile");
    } else if (alt === "Owl #13") {
      navigate("/pete-profile");
    } else if (alt === "AI For All" || alt === "Owl #10") {
      navigate("/yannis-profile");
    } else if (alt === "Brand Marketing Expert" || alt === "Owl #4") {
      navigate("/profile-jess");
    } else if (alt === "Innovation Logistics") {
      navigate("/corey-profile");
    } else if (alt === "Blockchain Expert" || alt === "Owl #8") {
      navigate("/shaan-profile");
    } else if (alt === "Product Design Expert" || alt === "Owl #11") {
      navigate("/miguel-profile");
    } else if (alt === "Finance Expert" || alt === "Owl #5") {
      navigate("/jim-profile");
    } else if (alt === "Security Expert" || alt === "Owl #7") {
      navigate("/mike-mosher-profile");
    }
  };

  // Function to get member name and role based on alt text
  const getMemberInfo = (alt: string): { name: string, role: string, email: string } => {
    if (alt === "Owl #9" || alt === "Innovation Expert") {
      return { name: "Trey Fleming", role: "iSPAI Founder", email: "trey.fleming@ispai.io" };
    } else if (alt === "Owl #12" || alt === "Supply Chain and Law Expert") {
      return { name: "Morgan Eldred, PhD", role: "Advisor - IM, IP, and IT Law", email: "morgan@digitalenergy.ai" };
    } else if (alt === "CTO Team Member") {
      return { name: "Bryan Ellis", role: "iSPAI Chief Technology Officer", email: "bryan.ellis@ispai.io" };
    } else if (alt === "GTM Advisor" || alt === "Owl #2") {
      return { name: "Chris Clayton", role: "Advisor - GTM and Commercial", email: "chrisclaytonsmp@gmail.com" };
    } else if (alt === "Strategic Sourcing" || alt === "Owl #6") {
      return { name: "Karen Watson", role: "Chief Consumer", email: "karen.watson@ispai.io" };
    } else if (alt === "Owl #13") {
      return { name: "Pete Dumont", role: "Advisor - Construction and Capital Projects", email: "pdumont@prairiedogvp.com" };
    } else if (alt === "AI For All" || alt === "Owl #10") {
      return { name: "Yannis Kalfoglou, PhD", role: "Chief Technical Advisor", email: "yanniskalfoglou@gmail.com" };
    } else if (alt === "Brand Marketing Expert" || alt === "Owl #4") {
      return { name: "Jess Simm", role: "Chief Marketing Officer", email: "jess.simm@ispai.io" };
    } else if (alt === "Innovation Logistics") {
      return { name: "Corey Vickers", role: "iSPAI Chief Creator", email: "corey.vickers@ispai.io" };
    } else if (alt === "Blockchain Expert" || alt === "Owl #8") {
      return { name: "Shaan Ray", role: "Head of Web3", email: "shaan.ray@ispai.io" };
    } else if (alt === "Product Design Expert" || alt === "Owl #11") {
      return { name: "Miguel Escobedo", role: "Chief Product Officer", email: "miguel.escobedo@ispai.io" };
    } else if (alt === "Finance Expert" || alt === "Owl #5") {
      return { name: "Jim Lawnin", role: "CFO and Board of Advisors Chair", email: "jimlawnin@outlook.com" };
    } else if (alt === "Security Expert" || alt === "Owl #7") {
      return { name: "Mike Mosher", role: "Advisor - Security, Risk, and Operations", email: "michaelmosher@fastmail.com" };
    } else {
      return { name: "Team Member", role: "iSPAI Team", email: "info@ispai.io" };
    }
  };

  return (
    <div className="mb-6 bg-white p-6 rounded-lg shadow">
      <h2 className="text-xl font-semibold mb-4">mySPAI Team Profiles</h2>
      <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-4">
        {members.map((member, index) => {
          const memberInfo = getMemberInfo(member.alt);
          
          return (
            <Tooltip key={index}>
              <TooltipTrigger asChild>
                <div 
                  className="aspect-square relative cursor-pointer transition-transform hover:scale-150 duration-200"
                  onClick={() => handleImageClick(member.alt)}
                  data-alt={member.alt} // Add data attribute for debugging
                  style={{ 
                    backgroundImage: `url(${member.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderRadius: '0.5rem'
                  }}
                />
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-center">
                  <p className="font-bold">{memberInfo.name}</p>
                  <p className="text-sm">{memberInfo.role}</p>
                  <a 
                    href={`mailto:${memberInfo.email}`} 
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center justify-center mt-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Mail size={12} className="mr-1" />
                    {memberInfo.email}
                  </a>
                </div>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

export default TeamMembersGrid;
